import type { CSSProperties, ReactNode } from 'react';

import classNames from 'classnames';
import classnames from 'classnames';

import { AdditionalFields } from '@/shared/additionalFields';

import { SignupGDPRCompliance } from '../../signupElement/SignupGDPRCompliance';
import type { Preference, Price, StripeCardElementComponent, StripePaymentElementComponent, Tier } from '../../types';
import { input, inputSubmit } from '../Pricing.css';
import CheckoutPaymentSection from './CheckoutPaymentSection';

interface Props {
  email?: string;
  onEmailChange: (value: string) => void;
  taxId: string;
  onTaxIdChange: (value: string) => void;
  ctaText: string;
  isCtaDisabled: boolean;
  showTaxIdField: boolean;
  StripeCardElement?: StripeCardElementComponent;
  StripePaymentElement?: StripePaymentElementComponent;
  selectedTier?: Tier;
  selectedPrice?: Price;
  PaymentRequestButtonElement?: ReactNode;
  hasTermsOfService?: boolean;
  hasCustomTermsOfService?: string;
  customTermsOfServiceText?: string;
  inputTextColor?: CSSProperties['color'];
  inputPlaceholderColor?: CSSProperties['color'];
  inputBackgroundColor?: CSSProperties['backgroundColor'];
  additionalFieldsData?: Preference[];
  isAdditionalFieldsLabelVisible?: boolean;
  additionalFieldsStyle?: CSSProperties;
}

const InternalCheckout = ({
  email,
  onEmailChange,
  taxId,
  onTaxIdChange,
  isCtaDisabled,
  StripeCardElement,
  StripePaymentElement,
  selectedTier,
  selectedPrice,
  ctaText = 'Upgrade',
  showTaxIdField = false,
  PaymentRequestButtonElement,
  hasTermsOfService = false,
  hasCustomTermsOfService,
  customTermsOfServiceText,
  inputTextColor,
  inputPlaceholderColor,
  inputBackgroundColor,
  additionalFieldsData,
  isAdditionalFieldsLabelVisible = false,
  additionalFieldsStyle = {},
}: Props) => {
  if (!selectedTier || !selectedPrice) {
    return null;
  }

  const emailField = (
    <input
      name="email"
      defaultValue={email}
      onBlur={(e) => e.currentTarget.value && onEmailChange(e.currentTarget.value)}
      type="email"
      placeholder="Email"
      className={classnames(input, 'py-2 px-4')}
    />
  );

  const gdprField = hasTermsOfService && (
    <SignupGDPRCompliance
      hasCustomTermsOfService={hasCustomTermsOfService}
      customTermsOfServiceText={customTermsOfServiceText}
      defaultTOSConsentValue={true}
    />
  );

  const additionalFields =
    additionalFieldsData && additionalFieldsData.length > 0 ? (
      <AdditionalFields
        data={additionalFieldsData}
        style={additionalFieldsStyle ?? {}}
        isLabelVisible={isAdditionalFieldsLabelVisible}
      />
    ) : null;

  if (PaymentRequestButtonElement) {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {additionalFields}
        {PaymentRequestButtonElement}
      </div>
    );
  }

  if (selectedTier?.id === 'free') {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {additionalFields}
        {gdprField}
        <input
          disabled={isCtaDisabled}
          type="submit"
          value="Continue as free reader"
          className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
        />
      </div>
    );
  }

  if (selectedPrice?.premium_offer && !selectedPrice?.premium_offer.card_required) {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {additionalFields}
        {gdprField}
        <input
          disabled={isCtaDisabled}
          type="submit"
          value={ctaText}
          className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 max-w-md w-full">
      {emailField}
      {additionalFields}
      <CheckoutPaymentSection
        isInternalCheckout
        StripeCardElement={StripeCardElement}
        StripePaymentElement={StripePaymentElement}
        inputTextColor={inputTextColor}
        inputPlaceholderColor={inputPlaceholderColor}
        inputBackgroundColor={inputBackgroundColor}
        showTaxIdField={showTaxIdField}
        taxId={taxId}
        onTaxIdChange={onTaxIdChange}
        ctaText={ctaText}
        isCtaDisabled={isCtaDisabled}
      />
    </div>
  );
};

export default InternalCheckout;
